
import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { UserProfile, ViewState, Language, AIEngine } from '../types.ts';
import { PERSONALITY_CONFIG } from '../constants.tsx';
import { TRANSLATIONS } from '../translations.ts';
import { Activity, Battery, Sun, Target, Mic, ChevronLeft, Sparkles, Brain, Lightbulb, Compass, Scroll, Zap, Loader2, Cpu, Waves } from 'lucide-react';
import { sendMessageToHawre } from '../services/geminiService.ts';

interface Props {
  user: UserProfile;
  onNavigate: (view: ViewState) => void;
}

const Dashboard: React.FC<Props> = ({ user, onNavigate }) => {
  const [insight, setInsight] = useState<string>("...");
  const [loadingInsight, setLoadingInsight] = useState(false);

  const t = TRANSLATIONS[user.language] || TRANSLATIONS[Language.KURDISH] || {};

  const radarData = [
    { subject: t.physical || 'Physical', A: user.stats.physical, fullMark: 100 },
    { subject: t.mental || 'Mental', A: user.stats.mental, fullMark: 100 },
    { subject: t.social || 'Social', A: user.stats.social, fullMark: 100 },
    { subject: t.professional || 'Professional', A: user.stats.professional, fullMark: 100 },
    { subject: t.financial || 'Financial', A: user.stats.financial, fullMark: 100 },
  ];

  const personality = PERSONALITY_CONFIG[user.selectedPersonality];
  const activeEngineLabel = user.preferredEngine === AIEngine.DEEPSEEK ? t.deepseek_label : user.preferredEngine === AIEngine.GROK ? t.grok_label : t.gemini_label;

  // Personality Synergy logic (mocked based on level/xp)
  const synergyLevel = Math.min(Math.floor((user.xp / 5000) * 100), 100);

  useEffect(() => {
    const fetchInsight = async () => {
      setLoadingInsight(true);
      try {
        const stats = user.stats;
        const lowestDim = Object.keys(stats).reduce((a, b) => (stats[a as keyof typeof stats] < stats[b as keyof typeof stats] ? a : b));
        
        const langName = user.language === Language.KURDISH ? "Sorani Kurdish" : "Persian";

        const prompt = `User Stats: ${JSON.stringify(user.stats)}. 
          Weakest point: ${lowestDim}. 
          Give a one-sentence motivation in ${langName} using your personality (${t[personality.labelKey]}). Introduce yourself as "Hawre".`;

        const response = await sendMessageToHawre(user, [], prompt);
        setInsight(response || "...");
      } catch (e) {
        setInsight("...");
      } finally {
        setLoadingInsight(false);
      }
    };

    fetchInsight();
  }, [user.selectedPersonality, user.language, user.preferredEngine]);

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in pb-12 px-2 md:px-0">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight">
            {(t.welcome || 'سڵاو، {name}!').replace('{name}', user.name)}
          </h2>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <p className="text-slate-500 font-bold bg-slate-100 px-3 py-1 rounded-lg text-xs md:sm">
              {new Date().toLocaleDateString(user.language === Language.KURDISH ? 'ckb-IR' : 'fa-IR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <div className="flex items-center gap-1.5 text-[9px] md:text-[10px] font-black text-indigo-600 uppercase tracking-widest bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100">
              <Cpu className="w-3 h-3 md:w-3.5 md:h-3.5" />
              {activeEngineLabel}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="bg-white p-4 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-4 w-full md:w-auto">
            <div className={`p-3 md:p-4 rounded-2xl ${personality.bg} ${personality.color} relative overflow-hidden group`}>
              <div className="absolute inset-0 bg-white/20 animate-pulse-slow"></div>
              {personality.icon}
            </div>
            <div className="pr-2 flex-1">
              <div className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t.level || 'Level'} {user.level}</div>
              <div className="font-black text-slate-900 text-base md:text-lg">{user.xp} <span className="text-xs md:text-sm text-slate-400">{t.xp || 'XP'}</span></div>
              <div className="w-full md:w-32 h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-indigo-500 transition-all duration-1000" style={{ width: `${(user.xp % 1000) / 10}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Personality Synergy Meter */}
          <div className="lg:col-span-1 bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100 flex flex-col justify-between relative overflow-hidden">
              <div className="absolute bottom-0 right-0 p-4 opacity-5"><Waves className="w-24 h-24" /></div>
              <div>
                <h4 className="font-bold text-slate-400 text-xs uppercase tracking-widest mb-4">
                  {user.language === Language.KURDISH ? 'گونجانی کەسایەتی' : 'هم‌افزایی شخصیت'}
                </h4>
                <div className="text-3xl font-black text-indigo-600 mb-2">{synergyLevel}%</div>
                <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
                  {user.language === Language.KURDISH 
                    ? 'چۆنیەتی گونجانی هاوڕێ لەگەڵ شێوازی ژیانی تۆ.' 
                    : 'چگونگی انطباق هاورێ با الگوهای زندگی شما.'}
                </p>
              </div>
              <div className="mt-6 flex gap-1">
                {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className={`h-2 flex-1 rounded-full ${i < synergyLevel/10 ? 'bg-indigo-500' : 'bg-slate-100'}`}></div>
                ))}
              </div>
          </div>

          {/* AI Insight Banner */}
          <div className={`lg:col-span-3 p-6 md:p-8 rounded-[2.5rem] border-2 ${personality.border} ${personality.bg} bg-opacity-40 flex flex-col md:flex-row items-start gap-4 md:gap-6 relative overflow-hidden group shadow-xl transition-all hover:scale-[1.01]`}>
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-125 transition-transform duration-700">
              <Sparkles className="w-24 h-24 md:w-32 md:h-32" />
            </div>
            <div className={`p-4 rounded-2xl bg-white shadow-md ${personality.color} shrink-0`}>
              <Brain className="w-6 h-6 md:w-8 md:h-8" />
            </div>
            <div className="relative z-10 flex-1">
              <h4 className="font-black text-slate-900 text-lg mb-2 flex items-center gap-2">
                {t.ai_analysis || 'شیکاری هاوڕێ'}
                {loadingInsight && <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />}
              </h4>
              <p className="text-slate-800 text-base md:text-lg leading-relaxed italic font-bold">
                "{insight}"
              </p>
            </div>
          </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <div className="lg:col-span-2 bg-white p-6 md:p-10 rounded-[2.5rem] md:rounded-[3rem] shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-6 md:mb-8">
              <h3 className="font-black text-slate-800 text-lg md:text-xl flex items-center gap-3">
                <Activity className="w-6 h-6 text-indigo-500" />
                {t.balance || 'هاوسەنگی ژیان'}
              </h3>
              <div className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                {user.language === Language.KURDISH ? 'پێوەرە زیندووەکان' : 'شاخص‌های لحظه‌ای'}
              </div>
          </div>
          <div className="h-64 md:h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="subject" tick={{ fontFamily: 'Vazirmatn', fontSize: 11, fill: '#64748b', fontWeight: '900' }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                <Radar
                  name="User"
                  dataKey="A"
                  stroke={personality.themeColor}
                  fill={personality.themeColor}
                  fillOpacity={0.6}
                  animationBegin={300}
                  animationDuration={1500}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="flex flex-col gap-4 md:gap-6">
           {/* Philosophy Card */}
           <div className="bg-indigo-600 p-6 md:p-8 rounded-[2.5rem] text-white shadow-2xl shadow-indigo-100 group cursor-pointer overflow-hidden relative transform transition-all hover:translate-y-[-4px]" onClick={() => onNavigate(ViewState.PHILOSOPHY)}>
              <div className="absolute top-0 right-0 p-4 opacity-20 group-hover:scale-150 transition-transform duration-500"><Compass className="w-20 h-20 md:w-24 md:h-24" /></div>
              <h4 className="text-xl font-black flex items-center gap-3 mb-2"><Compass className="w-6 h-6" /> {t.philosophy_hub}</h4>
              <p className="text-indigo-100 text-xs md:text-sm font-bold opacity-80">{t.philosophy_hub_desc}</p>
              <div className="mt-6 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] bg-white/10 w-fit px-4 py-1.5 rounded-full">{t.continue} <ChevronLeft className="w-3 h-3" /></div>
           </div>
           
           {/* Prediction Lab Card - Highlighted Amber Button */}
           <div className="bg-amber-500 p-6 md:p-8 rounded-[2.5rem] text-white shadow-2xl shadow-amber-200 group cursor-pointer overflow-hidden relative transform transition-all hover:translate-y-[-4px]" onClick={() => onNavigate(ViewState.PREDICTION)}>
              <div className="absolute top-0 right-0 p-4 opacity-30 group-hover:scale-150 transition-transform duration-500"><Zap className="w-20 h-20 md:w-24 md:h-24 fill-current" /></div>
              <h4 className="text-xl font-black flex items-center gap-3 mb-2">
                <Zap className="w-6 h-6" /> 
                {t.prediction_lab_title}
              </h4>
              <p className="text-amber-50 font-bold opacity-90 text-xs md:text-sm">{t.prediction_lab_desc}</p>
              <div className="mt-6 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] bg-white/20 w-fit px-4 py-1.5 rounded-full border border-white/20">{t.continue} <ChevronLeft className="w-3 h-3" /></div>
           </div>

           {/* Legacy Card */}
           <div className="bg-slate-900 p-6 md:p-8 rounded-[2.5rem] text-white shadow-2xl shadow-slate-200 group cursor-pointer overflow-hidden relative transform transition-all hover:translate-y-[-4px]" onClick={() => onNavigate(ViewState.LEGACY)}>
              <div className="absolute top-0 right-0 p-4 opacity-20 group-hover:scale-150 transition-transform duration-500"><Scroll className="w-20 h-20 md:w-24 md:h-24" /></div>
              <h4 className="text-xl font-black flex items-center gap-3 mb-2"><Scroll className="w-6 h-6 text-amber-400" /> {t.legacy_hub}</h4>
              <p className="text-slate-400 font-bold text-xs md:text-sm">{t.legacy_hub_desc}</p>
              <div className="mt-6 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] bg-white/10 w-fit px-4 py-1.5 rounded-full">{t.continue} <ChevronLeft className="w-3 h-3" /></div>
           </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {[
          { view: ViewState.COMPANION_LIVE, label: t.companion_live, icon: Mic, bg: 'bg-rose-500 shadow-rose-200' },
          { view: ViewState.CALM, label: t.immediate_calm, icon: Sun, bg: 'bg-teal-500 shadow-teal-200' },
          { view: ViewState.TIMELINE, label: t.memory_record, icon: Activity, bg: 'bg-blue-500 shadow-blue-200' },
          { view: ViewState.SOCIAL_TRAINING, label: t.social_practice, icon: Target, bg: 'bg-indigo-500 shadow-indigo-200' }
        ].map((action, idx) => (
          <button 
            key={idx}
            onClick={() => onNavigate(action.view)}
            className={`p-6 md:p-8 ${action.bg} text-white rounded-[2.5rem] md:rounded-[3rem] shadow-xl flex flex-col items-center gap-3 md:gap-4 hover:scale-[1.05] active:scale-95 transition-all group relative overflow-hidden`}
          >
            <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
            <action.icon className="w-8 h-8 md:w-10 md:h-10 group-hover:rotate-12 transition-transform relative z-10" />
            <span className="font-black text-[11px] md:text-sm tracking-tight relative z-10 text-center leading-tight">{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
